import { endpoint } from '@/consts/api';
import { themeId as THEME_IDS } from '@/consts/shop';

let loadedKarteApiKey: string | null = null;
/** URL ?theme= で指定された API Key。affiliate 由来の異なる key への上書きを防止しつつ、同一 key へのリトライは許可する */
let lockedApiKey: string | null = null;
/** URL ?theme= で指定された themeId（正規化前の値）。GMO リダイレクト等での保存・復元に使用 */
let lockedThemeId: number | null = null;
/** API Key 未設定等で KARTE を利用できない場合に true */
let karteDisabled = false;
/** key 未確定時に sendKarteView() の引数をキューし、initKarte() 後に順次補填する */
interface CompensatingView {
  title?: string;
  values?: Record<string, unknown>;
}
let compensatingViews: CompensatingView[] = [];
// eslint-disable-next-line no-magic-numbers
const COMPENSATING_VIEWS_MAX = 20;
/** resetKarte() 後〜次回 initKarte() まで補填キューへの記録を抑止する */
let suppressCompensation = false;
/** edge.js のロードが完了し SDK が利用可能な状態 */
let sdkReady = false;
/** onerror が現在のロードにのみ作用するための世代カウンタ */
let currentLoadId = 0;

const VALID_THEME_IDS: ReadonlySet<number> = new Set(Object.values(THEME_IDS));

function resolveKarteApiKey(themeId: number): string | undefined {
  if (!VALID_THEME_IDS.has(themeId)) return undefined;
  // eslint-disable-next-line no-magic-numbers
  return themeId <= 13
    ? endpoint.KARTE_API_KEY_GROUP_A
    : endpoint.KARTE_API_KEY_GROUP_B;
}

/**
 * index.html の KARTE stub と同等の krt 関数を再生成する。
 * ログアウト時に旧 SDK ハンドラを破棄し、次回 edge.js ロードまでの
 * 呼び出しをキューに保持するために使用する。
 */
function reinstallKarteStub(): void {
  /* eslint-disable @typescript-eslint/no-explicit-any */
  const o: any = function () {
    const a = Array.prototype.slice.call(arguments);
    return o.x ? o.x.apply(null, a) : o.q.push(a);
  };
  o.q = [];
  o.i = Date.now();
  o.allow = function () {
    o.o = 'allow';
  };
  o.deny = function () {
    o.o = 'deny';
  };
  (window as any).krt = o;
  /* eslint-enable @typescript-eslint/no-explicit-any */
}

/**
 * themeId から Group A/B を判定し、対応する KARTE edge.js を動的にロードする。
 * - themeId 1〜13 → Group A
 * - themeId 14以上 → Group B
 *
 * @param isUrlOverride URL ?theme= による明示指定の場合 true を渡す。
 *   true の場合、以降の affiliate 由来の呼び出しは無視される。
 *
 * 同一 key での再呼び出しは no-op。
 * 異なる key が既にロード済みの場合は旧 SDK を破棄し新しい key で再初期化する。
 */
export function initKarte(themeId: number, isUrlOverride = false): void {
  if (karteDisabled) return;
  // resetKarte() 後の抑止状態を解除（新しいセッション開始）
  suppressCompensation = false;

  const apiKey = resolveKarteApiKey(themeId);
  if (!apiKey) {
    return;
  }

  if (loadedKarteApiKey === apiKey) return;

  // URL 指定で既にロック済みなら、同一 key へのリトライ以外は無視
  if (lockedApiKey && !isUrlOverride && apiKey !== lockedApiKey) return;

  // 異なる key が既にロード済みの場合、旧 SDK を破棄して再初期化する。
  // window.krt は新 SDK に引き継がれるため、以降の手動 view は
  // 正しい API Key に送信される。
  if (loadedKarteApiKey !== null) {
    resetKarte();
    // resetKarte() はログアウト用に suppressCompensation = true を設定するが、
    // key 切り替えではセッション継続中なので suppress を解除する。
    suppressCompensation = false;
  }

  loadedKarteApiKey = apiKey;
  if (isUrlOverride) {
    lockedApiKey = apiKey;
    lockedThemeId = themeId;
  }

  sdkReady = false;
  const script = document.createElement('script');
  script.src = `https://cdn-edge.karte.io/${apiKey}/edge.js`;
  script.async = true;
  script.dataset.options = JSON.stringify({ firstViewDisabled: true });
  const loadId = ++currentLoadId;
  script.onload = () => {
    if (loadId !== currentLoadId) return;
    sdkReady = true;
    // edge.js ロード成功後に、drop された view を順次補填
    if (compensatingViews.length > 0) {
      const views = compensatingViews.splice(0);
      for (const v of views) {
        sendKarteView(v.title, v.values);
      }
    }
  };
  script.onerror = () => {
    if (loadId !== currentLoadId) return;
    // 読み込み失敗時は key をクリアし再試行可能な状態に戻す。
    // compensatingViews はクリアしていないため次回 retry で復元される。
    loadedKarteApiKey = null;
  };
  document.head.appendChild(script);
}

/**
 * ログアウト時に呼び出し、KARTE の状態をリセットする。
 * 旧 SDK ハンドラを破棄し stub を再生成することで、
 * 次回 initKarte() 時に正しい API Key で再初期化される。
 */
export function resetKarte(): void {
  // 旧 script の onerror が発火しても無視されるよう世代を進める
  currentLoadId++;
  document
    .querySelectorAll<HTMLScriptElement>('script[src*="cdn-edge.karte.io"]')
    .forEach((s) => s.remove());
  loadedKarteApiKey = null;
  lockedApiKey = null;
  lockedThemeId = null;
  karteDisabled = false;
  compensatingViews = [];
  sdkReady = false;
  // ログアウト後の /login 遷移で pageAfterIn が補填フラグを立てないよう抑止
  suppressCompensation = true;
  reinstallKarteStub();
}

/**
 * URL ?theme= でロックされた themeId を返す。
 * GMO リダイレクト前に localStorage へ保存し、復帰後に復元するために使用する。
 */
export function getLockedThemeId(): number | null {
  return lockedThemeId;
}

/**
 * KARTE にページビューイベントを送信する。
 * initKarte() 前（API Key 未確定）の呼び出しは帰属先が不正確なため破棄される。
 */
export function sendKarteView(
  title?: string,
  values?: Record<string, unknown>,
): void {
  if (karteDisabled) return;

  // API Key 未確定の場合は帰属先が不正確なため破棄する（キューにも積まない）。
  if (!loadedKarteApiKey) return;

  // key 確定済みだが edge.js ロード未完了の場合は onload 後の補填対象として記録する。
  // resetKarte() 後〜initKarte() 前は抑止し、ログアウト画面の view が
  // 次セッションに紐づくのを防ぐ。
  if (!sdkReady) {
    if (
      !suppressCompensation &&
      compensatingViews.length < COMPENSATING_VIEWS_MAX
    ) {
      compensatingViews.push({ title, values });
    }
    return;
  }
  if (!window.krt) return;

  const viewData: Record<string, unknown> = {};
  if (title) viewData.title = title;
  if (values) Object.assign(viewData, values);

  if (Object.keys(viewData).length > 0) {
    window.krt('send', 'view', viewData);
  } else {
    window.krt('send', 'view');
  }
}
